import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  Badge,
  InputGroup,
  Card,
  Dropdown,
  Pagination,
} from "react-bootstrap";
import {
  FaTrashAlt,
  FaEdit,
  FaEye,
  FaPlus,
  FaSearch,
  FaEllipsisV,
  FaUserCircle,
  FaFilter,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCalendarAlt,
} from "react-icons/fa";
import { APICall, APICallWithImage } from "../../Utls/Api";
import { BiExport } from "react-icons/bi";

// Custom Avatar Component
const Avatar = ({ name, size = 40, className = "" }) => {
  const initials = name
    ? name
        .split(" ")
        .map((n) => n[0])
        .join("")
    : "";

  return (
    <div
      className={`rounded-circle bg-primary text-white d-flex align-items-center justify-content-center ${className}`}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        fontWeight: "bold",
      }}
    >
      {initials}
    </div>
  );
};

const EmployeesHero = () => {
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // Date range for "Export by Date"
  const [exportRange, setExportRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [exporting, setExporting] = useState(false);

  // Data states
  const [employees, setEmployees] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "success",
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Employee form data
  const [employeeData, setEmployeeData] = useState({
    name: "",
    email: "",
    password: "",
    supervisorId: "",
    file: null,
  });

  // Current employee being edited/deleted
  const [currentEmployee, setCurrentEmployee] = useState(null);

  // Table interaction states
  const [sortConfig, setSortConfig] = useState({
    key: "fullname",
    direction: "asc",
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(5);

  // Show alert message
  const showAlert = (message, variant = "success") => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ ...alert, show: false }), 3000);
  };

  // Fetch employees and supervisors on component mount
  useEffect(() => {
    fetchEmployees();
    fetchSupervisors();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await APICall("auth/employee/", "GET");
      setEmployees(response.employees || []);
    } catch (error) {
      showAlert("Failed to fetch employees", "danger");
      console.error("Error fetching employees:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSupervisors = async () => {
    try {
      const response = await APICall("auth/supervisor/all", "GET");
      setSupervisors(response.supervisors || []);
      // Set default supervisor if available
      if (response.supervisors?.length > 0) {
        setEmployeeData((prev) => ({
          ...prev,
          supervisorId: response.supervisors[0]._id,
        }));
      }
    } catch (error) {
      showAlert("Failed to fetch supervisors", "danger");
      console.error("Error fetching supervisors:", error);
    }
  };

  // Sort employees
  const sortedEmployees = useMemo(() => {
    let sortableItems = [...employees];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [employees, sortConfig]);

  // Filter employees based on search term
  const filteredEmployees = useMemo(() => {
    return sortedEmployees.filter(
      (employee) =>
        employee.fullname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [sortedEmployees, searchTerm]);

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredEmployees.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredEmployees.length / rowsPerPage);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ms-1" />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp className="ms-1" />
    ) : (
      <FaSortDown className="ms-1" />
    );
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleSelectAllRows = (e) => {
    if (e.target.checked) {
      setSelectedRows(currentRows.map((row) => row._id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleDeleteClick = (employee) => {
    setCurrentEmployee(employee);
    setShowDeleteModal(true);
  };

  const handleEditClick = (employee) => {
    setCurrentEmployee(employee);
    setEmployeeData({
      name: employee.fullname || "",
      email: employee.email || "",
      password: "", // Password is not retrieved for security reasons
      supervisorId: employee.supervisorId || supervisors[0]?._id || "",
      file: null,
    });
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    try {
      const data = {
        id: currentEmployee._id,
      };
      await APICall(`auth/employee/delete`, "POST", data);
      showAlert("Employee deleted successfully");
      fetchEmployees();
      // Remove deleted item from selection
      setSelectedRows((prev) =>
        prev.filter((rowId) => rowId !== currentEmployee._id)
      );
      setShowDeleteModal(false);
    } catch (error) {
      showAlert("Failed to delete employee", "danger");
      console.error("Error deleting employee:", error);
    }
  };

  const handleBulkDelete = () => {
    if (
      window.confirm(
        `Are you sure you want to delete ${selectedRows.length} employees?`
      )
    ) {
      // Implement bulk delete logic here
      showAlert(`${selectedRows.length} employees deleted`, "success");
      setSelectedRows([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmployeeData({ ...employeeData, [name]: value });
  };

  const handleFileChange = (e) => {
    setEmployeeData({ ...employeeData, file: e.target.files[0] });
  };

  const handleAddSubmit = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("name", employeeData.name);
      formData.append("email", employeeData.email);
      formData.append("password", employeeData.password);
      formData.append("supervisorId", employeeData.supervisorId);
      if (employeeData.file) {
        formData.append("file", employeeData.file);
      }

      await APICallWithImage("auth/employee/create", "POST", formData);

      showAlert("Employee created successfully");
      setShowAddModal(false);
      setEmployeeData({
        name: "",
        email: "",
        password: "",
        supervisorId: supervisors[0]?._id || "",
        file: null,
      });
      fetchEmployees();
    } catch (error) {
      showAlert(`Failed to create employee: ${error.message}`, "danger");
      console.error("Error creating employee:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("id", currentEmployee._id);
      formData.append("name", employeeData.name);
      formData.append("email", employeeData.email);
      if (employeeData.password) {
        formData.append("password", employeeData.password);
      }
      formData.append("supervisorId", employeeData.supervisorId);
      if (employeeData.file) {
        formData.append("file", employeeData.file);
      }

      await APICallWithImage("auth/employee/update", "POST", formData);

      showAlert("Employee updated successfully");
      setShowEditModal(false);
      setEmployeeData({
        name: "",
        email: "",
        password: "",
        supervisorId: supervisors[0]?._id || "",
        file: null,
      });
      fetchEmployees();
    } catch (error) {
      showAlert(`Failed to update employee: ${error.message}`, "danger");
      console.error("Error updating employee:", error);
    } finally {
      setLoading(false);
    }
  };

  const hanldeExport = async (id, range = null) => {
    try {
      setExporting(true);
      const payload = { userId: id };
      if (range?.startDate && range?.endDate) {
        payload.startDate = range.startDate;
        payload.endDate = range.endDate;
      }

      const blob = await APICall(
        "employee/genrateExcelData",
        "POST",
        payload,
        "blob"
      );

      // The API answers errors with JSON, so don't save those as a .xlsx file
      if (blob?.type?.includes("json")) {
        const text = await blob.text();
        showAlert(JSON.parse(text)?.message || "Failed to export", "danger");
        return false;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = range?.startDate
        ? `employee_report_${range.startDate}_to_${range.endDate}.xlsx`
        : "supervisors_report.xlsx"; // Optional: dynamic filename
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error("Error exporting Excel file:", error);
      alert("Failed to export Excel file.");
      return false;
    } finally {
      setExporting(false);
    }
  };

  const handleExportByDateClick = (employee) => {
    setCurrentEmployee(employee);
    setExportRange({ startDate: "", endDate: "" });
    setShowExportModal(true);
  };

  const handleExportByDateSubmit = async () => {
    if (!exportRange.startDate || !exportRange.endDate) {
      showAlert("Please select both a start and an end date", "danger");
      return;
    }
    if (exportRange.startDate > exportRange.endDate) {
      showAlert("Start date cannot be after end date", "danger");
      return;
    }

    const exported = await hanldeExport(currentEmployee._id, exportRange);
    if (exported) {
      setShowExportModal(false);
    }
  };

  return (
    <div className="p-4">
      {/* Alert Notification */}
      {alert.show && (
        <Alert
          variant={alert.variant}
          className="fade-in-alert"
          onClose={() => setAlert({ ...alert, show: false })}
          dismissible
        >
          {alert.message}
        </Alert>
      )}

      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Employee Management</h2>
          <p className="text-muted mb-0">
            Manage your team members and their permissions
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowAddModal(true)}
          disabled={loading}
          className="d-flex align-items-center"
        >
          <FaPlus className="me-2" />
          Add Employee
        </Button>
      </div>

      {/* Search and Filter Bar */}
      <Card className="mb-4 shadow-sm">
        <Card.Body className="p-3">
          <div className="d-flex justify-content-between align-items-center">
            <InputGroup style={{ width: "300px" }}>
              <InputGroup.Text className="bg-white">
                <FaSearch className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-start-0"
              />
            </InputGroup>

            <div className="d-flex align-items-center gap-2">
              {selectedRows.length > 0 && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleBulkDelete}
                  className="d-flex align-items-center"
                >
                  <FaTrashAlt className="me-1" />
                  Delete ({selectedRows.length})
                </Button>
              )}
              <Dropdown>
                <Dropdown.Toggle
                  variant="outline-secondary"
                  className="d-flex align-items-center"
                >
                  <FaFilter className="me-2" />
                  Filters
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item>Active</Dropdown.Item>
                  <Dropdown.Item>Inactive</Dropdown.Item>
                  <Dropdown.Item>Recently Added</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Employees Table */}
      <Card className="shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading employees...</p>
            </div>
          ) : (
            <>
              <div className="rounded">
                <Table hover className="mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th className="ps-4" style={{ width: "50px" }}>
                        <Form.Check
                          type="checkbox"
                          onChange={handleSelectAllRows}
                          checked={
                            selectedRows.length === currentRows.length &&
                            currentRows.length > 0
                          }
                        />
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => requestSort("fullname")}
                      >
                        <div className="d-flex align-items-center">
                          Employee
                          {getSortIcon("fullname")}
                        </div>
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => requestSort("email")}
                      >
                        <div className="d-flex align-items-center">
                          Email
                          {getSortIcon("email")}
                        </div>
                      </th>
                      <th>Status</th>
                      <th>Role</th>
                      <th className="text-end pe-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.length > 0 ? (
                      currentRows.map((employee) => (
                        <tr
                          key={employee._id}
                          className={
                            selectedRows.includes(employee._id)
                              ? "table-active"
                              : ""
                          }
                        >
                          <td className="ps-4">
                            <Form.Check
                              type="checkbox"
                              checked={selectedRows.includes(employee._id)}
                              onChange={() => handleSelectRow(employee._id)}
                            />
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <Avatar
                                name={employee.fullname}
                                className="me-3"
                              />
                              <div>
                                <div className="fw-semibold">
                                  {employee.fullname}
                                </div>
                                <small className="text-muted">
                                  ID: {employee._id.substring(0, 8)}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td>{employee.email}</td>
                          <td>
                            <Badge bg="success" pill>
                              Active
                            </Badge>
                          </td>
                          <td>Employee</td>
                          <td className="text-end pe-4">
                            <Dropdown>
                              <Dropdown.Toggle
                                variant="light"
                                size="sm"
                                className="px-2"
                              >
                                <FaEllipsisV />
                              </Dropdown.Toggle>
                              <Dropdown.Menu align="end">
                                <Dropdown.Item onClick={() => {hanldeExport(employee._id)}}>
                                  <BiExport className="me-2 text-primary" />
                                 Export
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handleExportByDateClick(employee)}
                                >
                                  <FaCalendarAlt className="me-2 text-info" />
                                  Export by Date
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handleEditClick(employee)}
                                >
                                  <FaEdit className="me-2 text-warning" />
                                  Edit
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handleDeleteClick(employee)}
                                >
                                  <FaTrashAlt className="me-2 text-danger" />
                                  Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-5">
                          <FaUserCircle size={48} className="text-muted mb-3" />
                          <h5>No employees found</h5>
                          <p className="text-muted">
                            Try adjusting your search or add a new employee
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {filteredEmployees.length > rowsPerPage && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <div className="text-muted">
                    Showing {indexOfFirstRow + 1} to{" "}
                    {Math.min(indexOfLastRow, filteredEmployees.length)} of{" "}
                    {filteredEmployees.length} entries
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                    />
                    {Array.from({ length: totalPages }, (_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Add Employee Modal */}
      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h4">Add New Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Form>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter full name"
                    name="name"
                    value={employeeData.name}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Email address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email"
                    name="email"
                    value={employeeData.email}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter password"
                    name="password"
                    value={employeeData.password}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Supervisor</Form.Label>
                  <Form.Select
                    name="supervisorId"
                    value={employeeData.supervisorId}
                    onChange={handleChange}
                    disabled={loading || supervisors.length === 0}
                  >
                    {supervisors.map((supervisor) => (
                      <option key={supervisor._id} value={supervisor._id}>
                        {supervisor.fullname ||
                          supervisor.name ||
                          supervisor.email}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12">
                <Form.Group className="mb-3">
                  <Form.Label>Profile Picture</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={handleFileChange}
                    disabled={loading}
                    accept="image/*"
                  />
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="light"
            onClick={() => setShowAddModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddSubmit}
            disabled={
              loading ||
              !employeeData.name ||
              !employeeData.email ||
              !employeeData.password ||
              !employeeData.supervisorId
            }
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  size="sm"
                  animation="border"
                  role="status"
                  className="me-2"
                />
                Adding...
              </>
            ) : (
              "Add Employee"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Employee Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h4">Edit Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Form>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter full name"
                    name="name"
                    value={employeeData.name}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Email address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email"
                    name="email"
                    value={employeeData.email}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>
                    New Password (leave blank to keep current)
                  </Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter new password"
                    name="password"
                    value={employeeData.password}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Supervisor</Form.Label>
                  <Form.Select
                    name="supervisorId"
                    value={employeeData.supervisorId}
                    onChange={handleChange}
                    disabled={loading || supervisors.length === 0}
                  >
                    {supervisors.map((supervisor) => (
                      <option key={supervisor._id} value={supervisor._id}>
                        {supervisor.fullname ||
                          supervisor.name ||
                          supervisor.email}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12">
                <Form.Group className="mb-3">
                  <Form.Label>Profile Picture</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={handleFileChange}
                    disabled={loading}
                    accept="image/*"
                  />
                  <Form.Text className="text-muted">
                    Current:{" "}
                    {currentEmployee?.profilePicture ? "Uploaded" : "None"}
                  </Form.Text>
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="light"
            onClick={() => setShowEditModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleEditSubmit}
            disabled={
              loading ||
              !employeeData.name ||
              !employeeData.email ||
              !employeeData.supervisorId
            }
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  size="sm"
                  animation="border"
                  role="status"
                  className="me-2"
                />
                Updating...
              </>
            ) : (
              "Update Employee"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Export by Date Modal */}
      <Modal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h4">Export by Date</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <p className="text-muted">
            Select the period to export for{" "}
            <strong>
              {currentEmployee?.fullname || currentEmployee?.email}
            </strong>
            .
          </p>
          <Form>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>From</Form.Label>
                  <Form.Control
                    type="date"
                    value={exportRange.startDate}
                    max={exportRange.endDate || undefined}
                    onChange={(e) =>
                      setExportRange({
                        ...exportRange,
                        startDate: e.target.value,
                      })
                    }
                    disabled={exporting}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>To</Form.Label>
                  <Form.Control
                    type="date"
                    value={exportRange.endDate}
                    min={exportRange.startDate || undefined}
                    onChange={(e) =>
                      setExportRange({
                        ...exportRange,
                        endDate: e.target.value,
                      })
                    }
                    disabled={exporting}
                  />
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="light"
            onClick={() => setShowExportModal(false)}
            disabled={exporting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleExportByDateSubmit}
            disabled={
              exporting || !exportRange.startDate || !exportRange.endDate
            }
          >
            {exporting ? (
              <>
                <Spinner
                  as="span"
                  size="sm"
                  animation="border"
                  role="status"
                  className="me-2"
                />
                Exporting...
              </>
            ) : (
              "Export"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete employee{" "}
            <strong>
              {currentEmployee?.fullname || currentEmployee?.email}
            </strong>
            ?
          </p>
          <p className="text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={loading}>
            {loading ? (
              <>
                <Spinner
                  as="span"
                  size="sm"
                  animation="border"
                  role="status"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Custom CSS for animations */}
      <style jsx>{`
        .fade-in-alert {
          animation: fadeIn 0.3s;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .table-responsive {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
      `}</style>
    </div>
  );
};

export default EmployeesHero;
